/**
 * ProceduralSquads — procedural top-down squad chassis sprites, 4 archetypes
 * × 2 factions = 8 frames in the `squads` atlas. Consumed by SpriteLayer
 * (`syncSquads`). Each frame is drawn facing +X so the consumer rotates by
 * `squad.facing` for the in-world heading.
 *
 * Visual direction: cinematic sci-fi military. Each archetype has a unique,
 * instantly-readable silhouette: Recon = sleek diamond stealth hull + long
 * optic; Assault = wide hex armour + dual shoulder cannons; Engineer = boxy
 * mech chassis + three articulated tool arms + holographic display; Shield =
 * compact body dominated by a huge energy-barrier forward shield.
 *
 * Split out of ProceduralAtlasExtras to keep that file under the 600-line cap.
 * Shares the palette + canvas helpers from ProceduralAtlasExtras.ts.
 */

import type Phaser from 'phaser';

import {
  ATLAS_SCALE,
  C_DARK, C_OUTLINE, C_P0, C_P0_BRIGHT, C_P1, C_P1_BRIGHT, C_PLATE_LIGHT,
  type Ctx, TWO_PI,
  circ, halo, hexA, makeCanvas,
} from './ProceduralAtlasExtras.js';

export function genSquads(scene: Phaser.Scene): void {
  const FW = 64, FH = 64;
  const FWS = FW * ATLAS_SCALE, FHS = FH * ATLAS_SCALE;
  const v: Array<[string, string, string, string]> = [
    ['recon_p0',    'recon',    C_P0, C_P0_BRIGHT],
    ['recon_p1',    'recon',    C_P1, C_P1_BRIGHT],
    ['assault_p0',  'assault',  C_P0, C_P0_BRIGHT],
    ['assault_p1',  'assault',  C_P1, C_P1_BRIGHT],
    ['engineer_p0', 'engineer', C_P0, C_P0_BRIGHT],
    ['engineer_p1', 'engineer', C_P1, C_P1_BRIGHT],
    ['shield_p0',   'shield',   C_P0, C_P0_BRIGHT],
    ['shield_p1',   'shield',   C_P1, C_P1_BRIGHT],
  ];
  const tex = makeCanvas(scene, 'squads', FWS, FHS, v.length);
  tex.context.scale(ATLAS_SCALE, ATLAS_SCALE);
  v.forEach(([, arch, c, b], i) => drawSquadIcon(tex.context, i * FW + FW / 2, FH / 2, arch, c, b));
  tex.refresh();
  v.forEach(([name], i) => tex.add(name, 0, i * FWS, 0, FWS, FHS));
}

function drawSquadIcon(
  ctx: Ctx, cx: number, cy: number, archetype: string, color: string, bright: string,
): void {
  // Soft contact shadow — gradient ellipse so it reads as a ground shadow, not a hole
  ctx.save();
  ctx.translate(cx + 2, cy + 4);
  ctx.scale(1, 7 / 16);
  const shadowGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, 16);
  shadowGrad.addColorStop(0, 'rgba(0,0,0,0.55)');
  shadowGrad.addColorStop(0.55, 'rgba(0,0,0,0.22)');
  shadowGrad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = shadowGrad;
  ctx.fillRect(-16, -16, 32, 32);
  ctx.restore();
  if (archetype === 'engineer')     drawEngineerSprite(ctx, cx, cy, color, bright);
  else if (archetype === 'shield')  drawShieldSprite(ctx, cx, cy, color, bright);
  else if (archetype === 'assault') drawAssaultSprite(ctx, cx, cy, color, bright);
  else                              drawReconSprite(ctx, cx, cy, color, bright);
}

// ---------------------------------------------------------------------------
// Recon — sleek stealth diamond hull, cloaking shimmer, long-barrel optic
// ---------------------------------------------------------------------------
function drawReconSprite(ctx: Ctx, cx: number, cy: number, color: string, bright: string): void {
  // Afterburn thruster trail (back-left)
  const thr = ctx.createLinearGradient(cx - 20, cy, cx - 5, cy);
  thr.addColorStop(0, hexA(color, 0)); thr.addColorStop(0.5, hexA(bright, 0.3));
  thr.addColorStop(1, hexA(bright, 0.65));
  ctx.fillStyle = thr;
  ctx.beginPath();
  ctx.moveTo(cx - 18, cy - 3.5); ctx.lineTo(cx - 6, cy - 5.5);
  ctx.lineTo(cx - 6, cy + 5.5);  ctx.lineTo(cx - 18, cy + 3.5);
  ctx.closePath(); ctx.fill();

  // Cloaking shimmer — semi-transparent offset halos
  ctx.globalAlpha = 0.18;
  for (let k = 0; k < 3; k++) {
    ctx.strokeStyle = bright; ctx.lineWidth = 0.9;
    ctx.beginPath();
    ctx.ellipse(cx - 2, cy, 10 + k * 2.5, 7 + k, 0.12, -Math.PI * 0.65, Math.PI * 0.65);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  // Swept-back stabiliser fins (top + bottom)
  ctx.fillStyle = '#1a2535';
  for (const sy of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(cx - 8, cy + sy * 6); ctx.lineTo(cx + 2, cy + sy * 11);
    ctx.lineTo(cx + 8, cy + sy * 8); ctx.lineTo(cx, cy + sy * 5);
    ctx.closePath(); ctx.fill();
    ctx.strokeStyle = hexA(color, 0.55); ctx.lineWidth = 0.8; ctx.stroke();
  }

  // Main chassis — elongated diamond, pointing right
  const cg = ctx.createLinearGradient(cx - 12, cy - 7, cx + 15, cy + 7);
  cg.addColorStop(0, C_DARK); cg.addColorStop(0.4, '#1a2535');
  cg.addColorStop(0.75, color); cg.addColorStop(1, bright);
  ctx.fillStyle = cg;
  ctx.beginPath();
  ctx.moveTo(cx + 15, cy);           // nose
  ctx.lineTo(cx + 3,  cy - 10);      // front top
  ctx.lineTo(cx - 11, cy - 6);       // rear top
  ctx.lineTo(cx - 14, cy);           // tail
  ctx.lineTo(cx - 11, cy + 6);       // rear bottom
  ctx.lineTo(cx + 3,  cy + 10);      // front bottom
  ctx.closePath(); ctx.fill();
  // V1.6 — single hairline outline. Thin enough that it never reads as a
  // banded rim against busy backgrounds; just enough to define the
  // silhouette against the dark hex map.
  ctx.strokeStyle = '#e8f4ff'; ctx.lineWidth = 1; ctx.stroke();

  // Top faction stripe
  ctx.globalAlpha = 0.9; ctx.strokeStyle = bright; ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - 10, cy - 5.5); ctx.lineTo(cx + 2, cy - 9.5); ctx.lineTo(cx + 14, cy - 1);
  ctx.stroke(); ctx.globalAlpha = 1;

  // Scope housing — sits above nose
  ctx.fillStyle = '#0e1218';
  ctx.beginPath(); ctx.arc(cx + 9, cy - 4, 3.8, 0, TWO_PI); ctx.fill();
  ctx.strokeStyle = color; ctx.lineWidth = 1.2;
  ctx.beginPath(); ctx.arc(cx + 9, cy - 4, 3.8, 0, TWO_PI); ctx.stroke();
  circ(ctx, cx + 9, cy - 4, 1.9, bright);
  ctx.globalAlpha = 0.9; circ(ctx, cx + 10, cy - 4.8, 0.75, '#ffffff'); ctx.globalAlpha = 1;
  halo(ctx, cx + 9, cy - 4, 6, bright, 0.4);

  // Long suppressed sniper barrel
  ctx.fillStyle = '#252c38';
  ctx.beginPath();
  ctx.moveTo(cx + 13, cy - 1.8); ctx.lineTo(cx + 27, cy - 1.3);
  ctx.lineTo(cx + 27, cy + 1.3); ctx.lineTo(cx + 13, cy + 1.8);
  ctx.closePath(); ctx.fill();
  ctx.strokeStyle = '#0a0c10'; ctx.lineWidth = 0.8; ctx.stroke();
  // Suppressor tip
  ctx.fillStyle = '#18202a';
  ctx.beginPath(); ctx.arc(cx + 27, cy, 2.4, 0, TWO_PI); ctx.fill();
  // Laser sight dot
  ctx.globalAlpha = 0.9; circ(ctx, cx + 27, cy, 0.9, '#40ff80'); ctx.globalAlpha = 1;

  // Dorsal antenna sweep
  ctx.strokeStyle = '#3a4a5a'; ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - 3, cy - 6); ctx.lineTo(cx - 3, cy - 15); ctx.lineTo(cx + 4, cy - 12);
  ctx.stroke();
  halo(ctx, cx - 3, cy - 15, 5, bright, 0.65);
  circ(ctx, cx - 3, cy - 15, 1.3, bright);

  // Rim light
  ctx.globalAlpha = 0.5; ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - 10, cy - 5.5); ctx.lineTo(cx + 2, cy - 9.5); ctx.stroke();
  ctx.globalAlpha = 1;
}

// ---------------------------------------------------------------------------
// Assault — wide hex armour, dual shoulder cannons, glowing combat core
// ---------------------------------------------------------------------------
function drawAssaultSprite(ctx: Ctx, cx: number, cy: number, color: string, bright: string): void {
  // Rear power cell / ammo block
  ctx.fillStyle = '#171f2c'; ctx.fillRect(cx - 21, cy - 9, 10, 18);
  ctx.strokeStyle = '#2a3440'; ctx.lineWidth = 1; ctx.strokeRect(cx - 21, cy - 9, 10, 18);
  // Power indicator LEDs
  for (let k = 0; k < 4; k++) {
    ctx.fillStyle = k < 3 ? bright : '#2a3440';
    ctx.globalAlpha = 0.85;
    ctx.fillRect(cx - 19, cy - 6 + k * 4, 2, 2.5);
  }
  ctx.globalAlpha = 1;

  // Main hex chassis
  const cg = ctx.createRadialGradient(cx - 3, cy - 3, 3, cx, cy, 17);
  cg.addColorStop(0, C_PLATE_LIGHT); cg.addColorStop(0.35, color); cg.addColorStop(1, C_DARK);
  ctx.fillStyle = cg;
  ctx.beginPath();
  ctx.moveTo(cx + 17, cy - 5); ctx.lineTo(cx + 14, cy - 14);
  ctx.lineTo(cx - 4,  cy - 16); ctx.lineTo(cx - 18, cy - 7);
  ctx.lineTo(cx - 18, cy + 7);  ctx.lineTo(cx - 4,  cy + 16);
  ctx.lineTo(cx + 14, cy + 14); ctx.lineTo(cx + 17, cy + 5);
  ctx.closePath(); ctx.fill();
  ctx.strokeStyle = '#e8f4ff'; ctx.lineWidth = 1; ctx.stroke();

  // Armour panel seams
  ctx.strokeStyle = C_OUTLINE; ctx.lineWidth = 1; ctx.globalAlpha = 0.6;
  ctx.beginPath();
  ctx.moveTo(cx - 3, cy - 15); ctx.lineTo(cx - 3, cy + 15);
  ctx.moveTo(cx - 11, cy - 9); ctx.lineTo(cx - 11, cy + 9);
  ctx.stroke(); ctx.globalAlpha = 1;

  // Shoulder pauldrons + mini-cannons (top and bottom)
  for (const sy of [-1, 1]) {
    const py = cy + sy * 13;
    const pg = ctx.createLinearGradient(cx - 5, py - sy * 2, cx + 13, py + sy * 4);
    pg.addColorStop(0, C_PLATE_LIGHT); pg.addColorStop(1, color);
    ctx.fillStyle = pg;
    ctx.beginPath();
    ctx.moveTo(cx - 4, py); ctx.lineTo(cx + 13, py);
    ctx.lineTo(cx + 14, py + sy * 6); ctx.lineTo(cx - 6, py + sy * 6);
    ctx.closePath(); ctx.fill();
    ctx.strokeStyle = C_OUTLINE; ctx.lineWidth = 1; ctx.stroke();
    // Cannon barrel
    ctx.fillStyle = '#1a212c';
    ctx.fillRect(cx + 12, py - sy * 0.5, 12, sy * 3.5 + (sy < 0 ? 3.5 : 0));
    ctx.strokeStyle = '#050708'; ctx.lineWidth = 0.8;
    ctx.strokeRect(cx + 12, py - sy * 0.5, 12, Math.abs(sy * 3.5) + (sy < 0 ? 3.5 : 0));
    // Muzzle brake
    circ(ctx, cx + 24, py + sy * 1.4, 1.9, '#060708');
    // Heat vent (2 slit marks)
    ctx.strokeStyle = bright; ctx.lineWidth = 0.7; ctx.globalAlpha = 0.55;
    ctx.beginPath(); ctx.moveTo(cx + 15, py); ctx.lineTo(cx + 15, py - sy * 2.5); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(cx + 19, py); ctx.lineTo(cx + 19, py - sy * 2.5); ctx.stroke();
    ctx.globalAlpha = 1;
  }

  // Glowing combat core — faction energy heart
  halo(ctx, cx + 3, cy, 12, bright, 0.75);
  circ(ctx, cx + 3, cy, 5.5, C_DARK);
  ctx.strokeStyle = color; ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.arc(cx + 3, cy, 5.5, 0, TWO_PI); ctx.stroke();
  circ(ctx, cx + 3, cy, 2.8, bright);
  circ(ctx, cx + 4, cy - 1, 1.0, '#ffffff');

  // Forward assault cannon
  ctx.fillStyle = '#1a212c'; ctx.fillRect(cx + 15, cy - 3.5, 13, 7);
  ctx.strokeStyle = C_OUTLINE; ctx.lineWidth = 1; ctx.strokeRect(cx + 15, cy - 3.5, 13, 7);
  ctx.fillStyle = '#050708'; ctx.fillRect(cx + 26, cy - 2.5, 2.5, 5);
  // Heat vents on barrel
  ctx.strokeStyle = bright; ctx.lineWidth = 0.8; ctx.globalAlpha = 0.6;
  for (let k = 0; k < 3; k++) {
    ctx.beginPath(); ctx.moveTo(cx + 17 + k * 3, cy - 3.5); ctx.lineTo(cx + 17 + k * 3, cy - 5.5); ctx.stroke();
  }
  ctx.globalAlpha = 1;

  // Visor slot
  ctx.fillStyle = '#000';
  ctx.beginPath(); ctx.ellipse(cx + 8, cy, 6, 2.5, 0, 0, TWO_PI); ctx.fill();
  ctx.fillStyle = bright; ctx.globalAlpha = 0.95;
  ctx.beginPath(); ctx.ellipse(cx + 9, cy, 4, 1.4, 0, 0, TWO_PI); ctx.fill();
  ctx.globalAlpha = 1; circ(ctx, cx + 11, cy - 0.8, 0.9, '#ffffff');

  // Top rim light
  ctx.globalAlpha = 0.45; ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(cx - 3, cy - 15.5); ctx.lineTo(cx + 13, cy - 13.5); ctx.stroke();
  ctx.globalAlpha = 1;
}

// ---------------------------------------------------------------------------
// Engineer — boxy multi-arm mech, holographic display, circuit-board chassis
// ---------------------------------------------------------------------------
function drawEngineerSprite(ctx: Ctx, cx: number, cy: number, color: string, bright: string): void {
  const half = 13;
  // Utility backpack (wider than body)
  ctx.fillStyle = '#151d28'; ctx.fillRect(cx - half - 8, cy - 10, 10, 20);
  ctx.strokeStyle = '#2a3440'; ctx.lineWidth = 1; ctx.strokeRect(cx - half - 8, cy - 10, 10, 20);
  // Power cells on backpack
  for (let k = 0; k < 4; k++) {
    ctx.fillStyle = k < 3 ? bright : '#2a3440'; ctx.globalAlpha = 0.8;
    ctx.fillRect(cx - half - 6, cy - 7 + k * 5, 3.5, 3.5);
  }
  ctx.globalAlpha = 1;

  // Boxy chassis with rounded corners
  const cg = ctx.createLinearGradient(cx - half, cy - half, cx + half, cy + half);
  cg.addColorStop(0, bright); cg.addColorStop(0.4, color); cg.addColorStop(1, C_DARK);
  ctx.fillStyle = cg;
  ctx.beginPath();
  const rad = 4;
  ctx.moveTo(cx - half + rad, cy - half); ctx.lineTo(cx + half - rad, cy - half);
  ctx.quadraticCurveTo(cx + half, cy - half, cx + half, cy - half + rad);
  ctx.lineTo(cx + half, cy + half - rad);
  ctx.quadraticCurveTo(cx + half, cy + half, cx + half - rad, cy + half);
  ctx.lineTo(cx - half + rad, cy + half);
  ctx.quadraticCurveTo(cx - half, cy + half, cx - half, cy + half - rad);
  ctx.lineTo(cx - half, cy - half + rad);
  ctx.quadraticCurveTo(cx - half, cy - half, cx - half + rad, cy - half);
  ctx.closePath(); ctx.fill();
  ctx.strokeStyle = '#e8f4ff'; ctx.lineWidth = 1; ctx.stroke();

  // Circuit-board traces
  ctx.strokeStyle = bright; ctx.lineWidth = 0.5; ctx.globalAlpha = 0.35;
  ctx.beginPath();
  for (let ky = -1; ky <= 1; ky++) {
    ctx.moveTo(cx - half + 2, cy + ky * 5); ctx.lineTo(cx + half - 2, cy + ky * 5);
  }
  ctx.stroke();
  ctx.fillStyle = bright; ctx.globalAlpha = 0.5;
  for (const [vx, vy] of [[-half + 4, -5], [-half + 9, -5], [-half + 4, 5], [half - 4, 0]] as [number, number][]) {
    ctx.beginPath(); ctx.arc(cx + vx, cy + vy, 1.2, 0, TWO_PI); ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Front trim strip
  ctx.fillStyle = bright; ctx.fillRect(cx + half - 4, cy - 9, 2.5, 18);
  ctx.strokeStyle = C_OUTLINE; ctx.lineWidth = 0.8; ctx.strokeRect(cx + half - 4, cy - 9, 2.5, 18);

  // Holographic display (floats above)
  const holox = cx + 2, holoy = cy - half - 5;
  halo(ctx, holox, holoy, 11, bright, 0.55);
  ctx.strokeStyle = bright; ctx.lineWidth = 1; ctx.globalAlpha = 0.9;
  ctx.strokeRect(holox - 7, holoy - 5, 14, 10);
  ctx.globalAlpha = 0.7; ctx.lineWidth = 0.8;
  for (let k = 0; k < 4; k++) {
    const ll = 3 + (k * 3) % 8;
    ctx.beginPath(); ctx.moveTo(holox - 5, holoy - 3 + k * 2); ctx.lineTo(holox - 5 + ll, holoy - 3 + k * 2); ctx.stroke();
  }
  ctx.globalAlpha = 0.95; ctx.fillStyle = bright;
  circ(ctx, holox + 5, holoy - 2, 1.1, bright);
  ctx.globalAlpha = 0.5; ctx.strokeStyle = bright; ctx.lineWidth = 0.8;
  ctx.beginPath(); ctx.moveTo(holox, holoy + 5); ctx.lineTo(holox, cy - half); ctx.stroke();
  ctx.globalAlpha = 1;

  // Three articulated tool arms
  const armColors = ['#ff9030', bright, '#60a0d0'];
  for (let arm = 0; arm < 3; arm++) {
    const ay = cy + (arm - 1) * 7.5;
    ctx.strokeStyle = '#252d38'; ctx.lineWidth = 2.8;
    ctx.beginPath(); ctx.moveTo(cx + half - 2, ay); ctx.lineTo(cx + half + 9, ay); ctx.stroke();
    if (arm === 0) {
      // Welding arc fork
      ctx.strokeStyle = armColors[arm] ?? bright; ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(cx + half + 7, ay); ctx.lineTo(cx + half + 12, ay - 3.5);
      ctx.moveTo(cx + half + 7, ay); ctx.lineTo(cx + half + 12, ay + 3.5);
      ctx.stroke();
      halo(ctx, cx + half + 12, ay, 5, '#ffc060', 0.9);
      ctx.globalAlpha = 0.9; circ(ctx, cx + half + 12, ay, 1.6, '#fff0a0'); ctx.globalAlpha = 1;
    } else if (arm === 1) {
      // Scanner disc
      circ(ctx, cx + half + 9, ay, 3.2, '#1a2028');
      ctx.strokeStyle = armColors[arm] ?? bright; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.arc(cx + half + 9, ay, 3.2, 0, TWO_PI); ctx.stroke();
      circ(ctx, cx + half + 9, ay, 1.3, armColors[arm] ?? bright);
    } else {
      // Gripper claw
      ctx.fillStyle = '#3a4050';
      ctx.fillRect(cx + half + 7, ay - 2.5, 4.5, 2);
      ctx.fillRect(cx + half + 7, ay + 0.5, 4.5, 2);
    }
  }

  // Visor
  ctx.fillStyle = '#000'; ctx.fillRect(cx + 2, cy - 2.5, 9, 5);
  ctx.fillStyle = bright; ctx.fillRect(cx + 3, cy - 1.5, 7, 3);
  ctx.fillStyle = '#ffffff'; ctx.fillRect(cx + 8, cy - 0.5, 2, 1);
  // Corner bolts
  ctx.fillStyle = '#1a2028';
  for (const [bx, by] of [[-half + 3, -half + 3], [half - 3, -half + 3], [-half + 3, half - 3], [half - 3, half - 3]] as [number, number][]) {
    ctx.beginPath(); ctx.arc(cx + bx, cy + by, 1.3, 0, TWO_PI); ctx.fill();
  }
  // Top rim light
  ctx.globalAlpha = 0.5; ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(cx - half + rad, cy - half); ctx.lineTo(cx + half - rad, cy - half); ctx.stroke();
  ctx.globalAlpha = 1;
}

// ---------------------------------------------------------------------------
// Shield — compact body dominated by a giant energy-barrier forward shield
// ---------------------------------------------------------------------------
function drawShieldSprite(ctx: Ctx, cx: number, cy: number, color: string, bright: string): void {
  // Generator backpack (back-left)
  ctx.fillStyle = '#141c28';
  ctx.beginPath(); ctx.ellipse(cx - 17, cy, 5.5, 11, 0, 0, TWO_PI); ctx.fill();
  ctx.strokeStyle = '#2a3440'; ctx.lineWidth = 1; ctx.stroke();
  // Generator glow vents
  for (const vy of [-5, 0, 5]) {
    ctx.strokeStyle = bright; ctx.lineWidth = 1; ctx.globalAlpha = 0.6;
    ctx.beginPath(); ctx.moveTo(cx - 21, cy + vy); ctx.lineTo(cx - 14, cy + vy); ctx.stroke();
  }
  ctx.globalAlpha = 1;
  halo(ctx, cx - 17, cy, 9, color, 0.4);

  // Compact armoured body disc
  const bg = ctx.createRadialGradient(cx - 5, cy - 4, 2, cx - 2, cy, 13);
  bg.addColorStop(0, C_PLATE_LIGHT); bg.addColorStop(0.5, color); bg.addColorStop(1, C_DARK);
  ctx.fillStyle = bg;
  ctx.beginPath(); ctx.arc(cx - 2, cy, 13, 0, TWO_PI); ctx.fill();
  ctx.strokeStyle = '#e8f4ff'; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.arc(cx - 2, cy, 13, 0, TWO_PI); ctx.stroke();

  // Energy shield barrier — dominant front feature
  ctx.save(); ctx.translate(cx + 10, cy);
  // Outer energy aura behind the plate
  const aura = ctx.createRadialGradient(3, 0, 0, 3, 0, 22);
  aura.addColorStop(0, hexA(bright, 0.65)); aura.addColorStop(0.5, hexA(color, 0.25));
  aura.addColorStop(1, hexA(color, 0));
  ctx.fillStyle = aura;
  ctx.beginPath();
  ctx.moveTo(-5, -18); ctx.bezierCurveTo(18, -18, 22, -9, 22, 0);
  ctx.bezierCurveTo(22, 9, 18, 18, -5, 18); ctx.closePath(); ctx.fill();

  // Shield plate (convex outward)
  const sg = ctx.createLinearGradient(-6, -16, 12, 16);
  sg.addColorStop(0, C_PLATE_LIGHT); sg.addColorStop(0.5, color); sg.addColorStop(1, '#0a0c10');
  ctx.fillStyle = sg;
  ctx.beginPath();
  ctx.moveTo(-5, -17); ctx.bezierCurveTo(14, -17, 18, -9, 18, 0);
  ctx.bezierCurveTo(18, 9, 14, 17, -5, 17); ctx.closePath();
  ctx.fill(); ctx.strokeStyle = C_OUTLINE; ctx.lineWidth = 2; ctx.stroke();

  // Bright faction edge rim
  ctx.strokeStyle = bright; ctx.lineWidth = 2; ctx.globalAlpha = 0.9;
  ctx.beginPath();
  ctx.moveTo(-5, -16); ctx.bezierCurveTo(13, -16, 16, -8, 16, 0);
  ctx.bezierCurveTo(16, 8, 13, 16, -5, 16); ctx.stroke();

  // Shield ward rune (diamond emblem)
  ctx.globalAlpha = 0.9; ctx.strokeStyle = bright; ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.moveTo(7, -9); ctx.lineTo(15, 0); ctx.lineTo(7, 9); ctx.lineTo(-1, 0); ctx.closePath();
  ctx.stroke();
  ctx.lineWidth = 0.8;
  ctx.beginPath(); ctx.moveTo(7, -7); ctx.lineTo(7, 7); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(2, 0); ctx.lineTo(12, 0); ctx.stroke();

  // Hex energy grid overlay on shield
  ctx.globalAlpha = 0.18; ctx.strokeStyle = bright; ctx.lineWidth = 0.5;
  for (let gy = -14; gy < 16; gy += 7) {
    for (let gx = -3; gx < 16; gx += 7) {
      ctx.beginPath();
      ctx.moveTo(gx + 3.5, gy); ctx.lineTo(gx + 7, gy + 3.5); ctx.lineTo(gx + 3.5, gy + 7);
      ctx.lineTo(gx, gy + 3.5); ctx.closePath(); ctx.stroke();
    }
  }
  ctx.globalAlpha = 1;

  // Edge rivets
  ctx.fillStyle = '#3a4050';
  for (const rv of [-12, -6, 0, 6, 12]) {
    ctx.beginPath(); ctx.arc(-4, rv, 1.3, 0, TWO_PI); ctx.fill();
  }
  ctx.restore();

  // Visor on body
  ctx.fillStyle = '#000';
  ctx.beginPath(); ctx.ellipse(cx + 4, cy, 4.5, 2.2, 0, 0, TWO_PI); ctx.fill();
  ctx.fillStyle = bright; ctx.globalAlpha = 0.95;
  ctx.beginPath(); ctx.ellipse(cx + 4.8, cy, 3, 1.3, 0, 0, TWO_PI); ctx.fill();
  ctx.globalAlpha = 1; circ(ctx, cx + 7, cy - 0.8, 0.85, '#ffffff');

  // Body rim light
  ctx.globalAlpha = 0.5; ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.arc(cx - 2, cy, 12, -Math.PI * 0.88, -Math.PI * 0.42); ctx.stroke();
  ctx.globalAlpha = 1;
}
